"use client";
import { useCallback, useEffect, useState } from "react"
import { NodeSelection, TextSelection } from "@tiptap/pm/state"
import { Fragment, Slice } from "@tiptap/pm/model"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

// --- Icons ---
import { ClipboardIcon } from "@/tiptap/icons/clipboard-icon"

export const COPY_TO_CLIPBOARD_SHORTCUT_KEY = "mod+c"

function canUseExecCommandCopy() {
  return (
    typeof document !== "undefined" &&
    typeof document.execCommand === "function"
  )
}

export function canUseClipboard() {
  return (
    typeof globalThis.navigator?.clipboard?.writeText === "function" ||
    canUseExecCommandCopy()
  )
}

function writeViaHiddenSelection(textContent, htmlContent) {
  if (!canUseExecCommandCopy()) {
    throw new Error("Clipboard API is unavailable")
  }

  const element = document.createElement(htmlContent ? "div" : "textarea")
  element.style.cssText = "position: fixed; left: -10000px; top: 10px"

  if (htmlContent) {
    element.setAttribute("contenteditable", "true")
    element.innerHTML = htmlContent
  } else {
    element.value = textContent || ""
  }

  document.body.appendChild(element)

  if (htmlContent) {
    const selection = window.getSelection()
    const range = document.createRange()
    range.selectNodeContents(element)
    selection?.removeAllRanges()
    selection?.addRange(range)
  } else {
    element.select()
  }

  let succeeded = false
  try {
    succeeded = document.execCommand("copy")
  } finally {
    element.remove()
    window.getSelection()?.removeAllRanges()
  }

  if (!succeeded) {
    throw new Error("Clipboard copy failed")
  }
}

/**
 * Writes text and optional HTML content to the clipboard
 * @param textContent The plain text content to write
 * @param htmlContent Optional HTML content to write
 * @returns Promise that resolves when the content is successfully written
 */
export async function writeToClipboard(textContent, htmlContent) {
  const clipboard = globalThis.navigator?.clipboard

  if (typeof clipboard?.writeText !== "function") {
    writeViaHiddenSelection(textContent, htmlContent)
    return
  }

  if (!canUseClipboard()) {
    throw new Error("Clipboard API is unavailable")
  }

  if (
    htmlContent &&
    typeof clipboard.write === "function" &&
    typeof ClipboardItem !== "undefined"
  ) {
    const clipboardItem = new ClipboardItem({
      "text/html": new Blob([htmlContent], { type: "text/html" }),
      "text/plain": new Blob([textContent || ""], { type: "text/plain" }),
    })

    try {
      await clipboard.write([clipboardItem])
      return
    } catch {
      // Fall back to plain text below when browsers reject rich clipboard writes.
    }
  }

  await clipboard.writeText(textContent || "")
}

/**
 * Checks if content can be copied in the current editor state
 */
export function canCopyContent(tr) {
  const { selection } = tr
  const { empty } = selection

  if (empty) return false

  return true
}

/**
 * Checks if formatting can be reset for a node
 */
export function canCopyToClipboard(editor) {
  if (!editor || !editor.isEditable) return false
  if (!canUseClipboard()) return false

  const tr = editor.state.tr
  return canCopyContent(tr);
}

/**
 * Helper function to extract content from selection or document
 */
export function extractContent(editor, copyWithFormatting = true) {
  const { selection } = editor.state
  const { $anchor } = selection

  let content = selection.content()

  if (selection.empty || selection instanceof TextSelection) {
    const node = $anchor.node(1)

    // We dont want to use node.content here because we dont want tiptap to split the node
    // We want the whole node as a slice
    content = new Slice(Fragment.from(node), 0, 0)
  }

  const selectedNode = selection instanceof NodeSelection ? selection.node : null
  const fallbackText = selectedNode?.type?.name === "image" ? selectedNode.attrs.src : ""
  const textContent =
    content.content.textBetween(0, content.content.size, "\n") || fallbackText
  const htmlContent = copyWithFormatting
    ? editor.view.serializeForClipboard(content).dom.innerHTML
    : undefined

  return { textContent, htmlContent }
}

/**
 * Copies content to clipboard
 */
export async function copyToClipboard(editor, copyWithFormatting = true) {
  if (!editor || !editor.isEditable) return false

  try {
    const { textContent, htmlContent } = extractContent(editor, copyWithFormatting)

    await writeToClipboard(textContent, htmlContent)
    return true
  } catch {
    return false
  }
}

/**
 * Determines if the copy to clipboard button should be shown
 */
export function shouldShowButton(props) {
  const { editor, hideWhenUnavailable } = props

  if (!editor) return false

  if (!hideWhenUnavailable) {
    return true
  }

  if (!editor.isEditable) return false

  if (!editor.isActive("code")) {
    return canCopyToClipboard(editor);
  }

  return true
}

/**
 * Custom hook that provides copy to clipboard functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage - no params needed
 * function MySimpleCopyButton() {
 *   const { isVisible, handleCopyToClipboard } = useCopyToClipboard()
 *
 *   if (!isVisible) return null
 *
 *   return <button onClick={handleCopyToClipboard}>Copy</button>
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedCopyButton() {
 *   const { isVisible, handleCopyToClipboard, label } = useCopyToClipboard({
 *     editor: myEditor,
 *     copyWithFormatting: false,
 *     hideWhenUnavailable: true,
 *     onCopied: () => console.log('Content copied!')
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <MyButton
 *       onClick={handleCopyToClipboard}
 *       aria-label={label}
 *     >
 *       Copy Content
 *     </MyButton>
 *   )
 * }
 * ```
 */
export function useCopyToClipboard(config) {
  const {
    editor: providedEditor,
    copyWithFormatting = true,
    hideWhenUnavailable = false,
    onCopied,
  } = config || {}

  const { editor } = useTiptapEditor(providedEditor)
  const [isVisible, setIsVisible] = useState(true)
  const canCopyToClipboardState = canCopyToClipboard(editor)

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({ editor, hideWhenUnavailable }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    };
  }, [editor, hideWhenUnavailable])

  const handleCopyToClipboard = useCallback(async () => {
    if (!editor) return false

    const success = await copyToClipboard(editor, copyWithFormatting)

    if (success) {
      onCopied?.()
    }

    return success
  }, [editor, copyWithFormatting, onCopied])

  return {
    isVisible,
    handleCopyToClipboard,
    canCopyToClipboard: canCopyToClipboardState,
    label: "Copy to clipboard",
    shortcutKeys: COPY_TO_CLIPBOARD_SHORTCUT_KEY,
    Icon: ClipboardIcon,
  }
}
